import { memo, useMemo } from 'react';
import SidebarWrapper from '@/shared/components/SidebarWrapper';
import { Trash } from '@/features/pages/components/layout/Sidebar/SidebarFooter';
import CollectionPageList from './CollectionPageList';
import CollectionSearchButton from './Search';
import CollectionDetailHeader from './CollectionDetailHeader';
import { useLocation, useParams } from 'react-router-dom';
import { useCollectionStore } from '../../stores/useCollectionStore';
import { useDisplayedCollectionStructure } from '../../hooks/useDisplayedCollectionStructure';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ChevronDown } from 'lucide-react';
import type { Collection } from '../../types/collection.types';

const CollectionSidebar = ({
  onBackClick,
  publicCollection,
}: {
  onBackClick: () => void;
  publicCollection?: Collection;
}) => {
  const collectionId = useParams<{ collectionId: string }>().collectionId as string;
  const privateCurrentCollection = useCollectionStore(state => state.currentCollection);
  const { structure: collectionStructure } = useDisplayedCollectionStructure();
  const location = useLocation();

  const isPublic = useMemo(() => location.pathname.includes('/shared'), [location.pathname]);
  const currentCollection = isPublic ? publicCollection : privateCurrentCollection;

  const renderSkeletonLoader = () => (
    <div>
      <div className="px-4 py-3 flex items-center justify-between">
        <Skeleton className="h-6 w-2/3" />
      </div>

      <div className="pt-2 px-2 mb-3">
        <Skeleton className="h-9 w-full rounded-md" />
      </div>

      {Array(3)
        .fill(0)
        .map((_, i) => (
          <div key={i}>
            <div className="px-[18px] mb-1 flex items-center space-x-1.5">
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
              <Skeleton className="h-5 w-24" />
            </div>

            <div className="px-3 ml-2 flex flex-col gap-2 mb-4">
              {Array(8)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={`section1-item-${i.toString()}`}
                    className="flex items-center space-x-2 p-1"
                  >
                    <Skeleton className="h-5 w-4" />
                    <Skeleton className="h-8 w-full rounded-md" />
                  </div>
                ))}
            </div>
          </div>
        ))}
    </div>
  );

  return (
    <SidebarWrapper>
      {isPublic && (!currentCollection || !collectionStructure.length) ? (
        renderSkeletonLoader()
      ) : !currentCollection ? null : (
        <>
          <CollectionDetailHeader
            collection={currentCollection}
            onBackClick={onBackClick}
            disabled={isPublic}
          />
          <div className="px-2 -mt-2 mb-2">
            <CollectionSearchButton collectionId={collectionId} />
          </div>
          <div
            className={cn(
              'flex-grow min-h-[calc(100vh-160px)]',
              isPublic && 'min-h-[calc(100vh-100px)]'
            )}
          >
            <CollectionPageList isPublicView={isPublic} />
          </div>
          {!isPublic && (
            <div className="pt-3 px-2 pb-2">
              <Trash collectionId={collectionId} />
            </div>
          )}
        </>
      )}
    </SidebarWrapper>
  );
};

export default memo(CollectionSidebar);
